import { useMemo, useState } from 'react'

import ProfilePhoto from 'src/shared/assets/images/profile-avatar-fallback.png'
import { BottomSheet } from 'src/shared/components/ui/bottom-sheet'
import TruncatedText from 'src/shared/components/ui/miscellaneous/truncated-text'
import { Dialog, DialogContent } from 'src/shared/components/ui/popups/dialog'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { formatMessageTime } from 'src/shared/utils/dates/format-message-time'

import type { UserZillowReview } from '../../../api/marketplace/use-user-reviews'
import { MiniZillow } from '../../../assets/mini-zillow'
import { ReviewStar } from '../../../assets/review-star'
import { getNumberOfStars } from '../util/get-number-of-stars'

const PublicPageReviewCard = ({ review }: { review: UserZillowReview }) => {
  const [showDialog, setShowDialog] = useState(false)
  const [showBottomSheet, setShowBottomSheet] = useState(false)
  const isMobile = useIsMobile()

  const onExpand = () => {
    if (isMobile) {
      setShowBottomSheet(true)
    } else {
      setShowDialog(true)
    }
  }

  const onClose = () => {
    setShowDialog(false)
    setShowBottomSheet(false)
  }

  const descriptionText = useMemo(() => {
    if (!review.serviceDescription) return ''

    if (!review.locationZipcode) return review.serviceDescription

    return `${review.serviceDescription} in ${review.locationZipcode}`
  }, [review.serviceDescription, review.locationZipcode])

  const dateText = useMemo(() => {
    if (!review.date) return ''
    // return new Date(review.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    return formatMessageTime(review.date)
  }, [review.date])

  return (
    <>
      <div
        className="w-full min-w-[280px] max-w-[calc(100vw-40px)] rounded-2xl border border-[#efefef] bg-white p-4 md:min-w-[325px] md:max-w-none"
        style={{
          boxShadow: '0px 3px 10px 1px rgba(27, 48, 41, 0.03)',
        }}
      >
        <div className="flex items-center gap-3">
          <img src={ProfilePhoto} alt="" className="size-[35px] rounded-full object-cover md:size-[50px]" />
          <div>
            <div className="text-[13px] font-semibold text-black md:text-base">
              {review.reviewerFullName || 'Anonymous'}
            </div>
            <div className="text-[11px] text-muted md:text-xs">{descriptionText}</div>
          </div>
        </div>
        <TruncatedText
          text={review.description || ''}
          numberOfLines={3}
          customOverflowComponent={
            <button
              onClick={onExpand}
              className="line-clamp-1 inline pl-1 text-[12px] font-semibold leading-[1.6] text-black underline md:text-[14px]"
            >
              Read more
            </button>
          }
          className="my-3 w-full text-[13px] leading-[1.6] text-black md:text-[15px]"
        />
        <div className="mt-3 flex flex-row items-center gap-1.5 text-xs text-muted md:text-sm">
          <MiniZillow className="size-[13px] md:size-[15px]" />
          <span className="flex flex-row gap-0.5">
            {[...Array(getNumberOfStars(review.rating ?? 0))].map((_, index) => (
              <ReviewStar key={index} className="size-[13px] md:size-[15px]" pattern="gold" />
            ))}
          </span>
          <span>{' · '}</span>
          <span>{dateText}</span>
        </div>
      </div>
      <BottomSheet open={showBottomSheet} close={onClose}>
        <div className="w-full bg-white">
          <div className="flex items-center gap-3">
            <img src={ProfilePhoto} alt="" className="size-[50px] rounded-full object-cover" />
            <div>
              <div className="font-semibold text-black">{review.reviewerFullName || 'Anonymous'}</div>
              <div className="text-xs text-muted">{descriptionText}</div>
            </div>
          </div>
          <div className="mt-3 text-[13px] text-black md:text-[15px]">{review.description}</div>
          <div className="mt-3 flex flex-row items-center gap-2 text-xs text-muted md:text-sm">
            <MiniZillow size={15} />
            <span className="flex flex-row gap-0.5">
              {[...Array(getNumberOfStars(review.rating ?? 0))].map((_, index) => (
                <ReviewStar key={index} size={15} pattern="gold" />
              ))}
            </span>
            <span>{' • '}</span>
            <span>{dateText}</span>
          </div>
        </div>
      </BottomSheet>
      <Dialog open={showDialog} onOpenChange={onClose}>
        <DialogContent className="p-6 sm:max-w-[425px]">
          <div className="w-full bg-white">
            <div className="flex items-center gap-3">
              <img src={ProfilePhoto} alt="" className="size-[50px] rounded-full object-cover" />
              <div>
                <div className="font-semibold text-black">{review.reviewerFullName || 'Anonymous'}</div>
                <div className="text-xs text-muted">{descriptionText}</div>
              </div>
            </div>
            <div className="mt-3 text-[13px] text-black md:text-[15px]">{review.description}</div>
            <div className="mt-3 flex flex-row items-center gap-2 text-xs text-muted md:text-sm">
              <MiniZillow size={15} />
              <span className="flex flex-row gap-0.5">
                {[...Array(getNumberOfStars(review.rating ?? 0))].map((_, index) => (
                  <ReviewStar key={index} size={15} pattern="gold" />
                ))}
              </span>
              <span>{' • '}</span>
              <span>{dateText}</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default PublicPageReviewCard
